import type { AnimationGroup } from "@babylonjs/core";

export interface PlayOptions {
  loop?: boolean;
  speedRatio?: number;
  onComplete?: () => void;
}

/**
 * Wraps a flat AnimationGroup[] (one per source Blender Action, as Babylon's
 * glTF loader produces them) into name-addressable playback, so callers
 * (JohnnyCharacter, later VignettePlayer) never touch Babylon's animation
 * API directly. Source clip names arrive as "CharacterArmature|Idle" (the
 * Blender action-naming convention baked into the export) — normalized here
 * so callers can just ask for "Idle".
 *
 * Playback is a straight stop/start, not a crossfade — sufficient for
 * Phase 2's "idles convincingly" bar. Real blending between clips (needed
 * once VignettePlayer starts switching Johnny between many actions per
 * vignette) is deferred until Phase 3 actually needs it.
 */
export class AnimationController {
  private readonly groups = new Map<string, AnimationGroup>();
  private current: AnimationGroup | null = null;

  constructor(animationGroups: AnimationGroup[]) {
    for (const group of animationGroups) {
      group.stop();
      this.groups.set(normalizeName(group.name), group);
    }
  }

  has(name: string): boolean {
    return this.groups.has(normalizeName(name));
  }

  listClips(): string[] {
    return [...this.groups.keys()];
  }

  playIdle(): void {
    this.playAction("Idle", { loop: true });
  }

  playAction(name: string, options: PlayOptions = {}): void {
    const group = this.groups.get(normalizeName(name));
    if (!group) {
      console.warn(`[Castaway] AnimationController: no clip named "${name}" (have: ${this.listClips().join(", ")})`);
      return;
    }
    if (this.current && this.current !== group) this.current.stop();
    this.current = group;
    group.start(options.loop ?? false, options.speedRatio ?? 1.0);
    if (!options.loop && options.onComplete) {
      group.onAnimationGroupEndObservable.addOnce(() => options.onComplete?.());
    }
  }

  stop(): void {
    this.current?.stop();
    this.current = null;
  }

  dispose(): void {
    for (const group of this.groups.values()) group.dispose();
  }
}

function normalizeName(name: string): string {
  const pipeIndex = name.indexOf("|");
  return (pipeIndex >= 0 ? name.slice(pipeIndex + 1) : name).trim();
}
